import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Seo from "@/components/Seo";
import { FadeUp, Overline, GoldRule } from "@/components/Reveal";
import ReviewStrip from "@/components/ReviewStrip";
import { IMG, SITE } from "@/constants/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (c) => `$${(c / 100).toFixed(0)}`;

export default function MealPrep() {
  const [menu, setMenu] = useState(undefined); // undefined = loading, null = none
  const [archive, setArchive] = useState([]);
  const [planId, setPlanId] = useState(null);
  const [alloc, setAlloc] = useState({});
  const [form, setForm] = useState({ name: "", email: "", phone: "", delivery_date: "", delivery_notes: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/mealprep/menu`).then(({ data }) => setMenu(data)).catch(() => setMenu(null));
    axios.get(`${API}/mealprep/archive`).then(({ data }) => setArchive(data)).catch(() => setArchive([]));
  }, []);

  const plan = menu?.items?.find((it) => it.id === planId) || null;
  const total = plan?.price_cents || 0;
  const mealCount = plan?.meal_count || null;
  const filled = Object.values(alloc).reduce((a, b) => a + b, 0);
  const mixReady = !mealCount || filled === mealCount;
  const today = new Date().toISOString().slice(0, 10);

  const pickPlan = (id) => {
    setPlanId(id);
    setAlloc({});
    setError("");
  };

  const setQty = (mealId, qty) => setAlloc((f) => ({ ...f, [mealId]: Math.max(0, Math.min(20, qty)) }));

  const onDate = (e) => {
    const v = e.target.value;
    if (v && new Date(`${v}T00:00:00`).getDay() !== 1) {
      setError("Deliveries are Mondays only — please pick a Monday.");
      e.target.value = form.delivery_date;
      return;
    }
    setError("");
    setForm((f) => ({ ...f, delivery_date: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy || !menu || !planId || !mixReady) return;
    setBusy(true);
    setError("");
    const allocations = mealCount
      ? Object.entries(alloc)
          .filter(([, qty]) => qty > 0)
          .map(([meal_id, qty]) => ({ meal_id, qty }))
      : [];
    try {
      const { data } = await axios.post(`${API}/mealprep/order`, {
        menu_id: menu.id,
        items: [{ item_id: planId, qty: 1 }],
        allocations,
        origin_url: window.location.origin,
        ...form,
      });
      if (data.checkout_url) window.location.href = data.checkout_url;
      else setError("Order received — we'll confirm payment details by email.");
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div>
      <Seo
        title="Meal Prep — Weekly Menus by Sita · Jersey City"
        description="Weekly meal prep plans from Sita — real meals made from scratch, lunch and dinner, delivered in Jersey City. Vegetarian and vegan substitutions available. Order through the site."
      />

      {/* ---------- Hero ---------- */}
      <section data-testid="mealprep-hero" className="mx-auto max-w-7xl px-6 pb-14 pt-14 md:px-10 md:pb-20 md:pt-20">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <FadeUp>
              <Overline>Meal prep · Weekly plans</Overline>
              <h1 className="mt-4 font-display text-5xl font-light leading-[1.05] text-forest md:text-6xl">
                Stock your week<br />with food that<br /><span className="italic text-terracotta">nourishes you.</span>
              </h1>
              <GoldRule className="mt-8" />
              <p className="mt-8 text-lg leading-relaxed text-ink/85">
                Let us stock your week with food that actually nourishes you. Real meals, made from scratch, waiting for
                you all week — lunch and dinner, delivered.
              </p>
            </FadeUp>
          </div>
          <FadeUp delay={0.15} className="lg:col-span-6">
            <div className="overflow-hidden rounded-3xl">
              <img src={IMG.privateMealPrep} alt="Sita meal prep, packed and ready for the week" className="aspect-[4/3] w-full object-cover" />
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ---------- This week ---------- */}
      <section data-testid="mealprep-menu" className="border-t border-forest/10">
        <div className="mx-auto max-w-7xl px-6 py-16 md:px-10 md:py-24">
          <FadeUp>
            <Overline>This week</Overline>
            <h2 className="mt-4 font-display text-4xl font-light text-forest md:text-5xl">
              {menu ? menu.week_label : menu === null ? "The next menu is in the works" : "Setting the table…"}
            </h2>
          </FadeUp>

          {menu === null && (
            <FadeUp delay={0.1}>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-ink/75">
                A new menu is posted every week. Check back shortly — or{" "}
                <Link to="/inquiries" className="underline decoration-gold underline-offset-4 text-terracotta">tell us what you love to eat</Link>{" "}
                and it might shape the next one.
              </p>
            </FadeUp>
          )}

          {menu && (
            <>
              {/* the week's meals */}
              <div className="mt-12 grid gap-8 md:grid-cols-3" data-testid="mealprep-meals">
                {(menu.meals || []).map((m, i) => (
                  <FadeUp key={i} delay={i * 0.1}>
                    <div className="h-full border-t border-gold pt-5">
                      <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-terracotta">Meal {["one", "two", "three", "four", "five"][i] || i + 1}</p>
                      <p className="mt-3 font-display text-2xl text-forest">{"{"} {m.name} {"}"}</p>
                      {m.description && <p className="mt-3 text-sm leading-relaxed text-ink/75">{m.description}</p>}
                    </div>
                  </FadeUp>
                ))}
              </div>

              {menu.notes && (
                <FadeUp delay={0.15}>
                  <p className="mt-10 rounded-2xl bg-gold/15 px-6 py-4 text-center text-sm leading-relaxed text-forest" data-testid="mealprep-notes">
                    {menu.notes}
                  </p>
                </FadeUp>
              )}

              {/* plans */}
              <FadeUp delay={0.1}>
                <h3 className="mt-16 font-display text-3xl font-light text-forest">Choose your plan</h3>
                <div className="mt-8 grid gap-6 md:grid-cols-3" data-testid="mealprep-plans">
                  {menu.items.map((it, i) => {
                    const selected = planId === it.id;
                    return (
                      <FadeUp key={it.id} delay={i * 0.1} className="h-full">
                        <button
                          type="button"
                          onClick={() => pickPlan(it.id)}
                          data-testid={`mealprep-plan-${i + 1}`}
                          aria-pressed={selected}
                          className={`flex w-full cursor-pointer flex-col items-center rounded-3xl border px-6 py-10 text-center transition-[border-color,background-color,transform,box-shadow] duration-300 hover:-translate-y-1 ${
                            selected
                              ? "border-terracotta bg-terracotta/5 shadow-lg shadow-terracotta/10"
                              : "border-forest/15 bg-white/50 hover:border-terracotta/50"
                          }`}
                        >
                          <span className="text-xs font-medium uppercase tracking-[0.22em] text-terracotta">{it.name}</span>
                          <span className="mt-4 font-display text-5xl font-light text-forest">{money(it.price_cents)}</span>
                          {it.description && <span className="mt-4 text-sm leading-relaxed text-ink/70">{it.description}</span>}
                        </button>
                      </FadeUp>
                    );
                  })}
                </div>
              </FadeUp>

              {/* order form */}
              <FadeUp delay={0.15}>
                <form
                  onSubmit={submit}
                  data-testid="mealprep-order-form"
                  className="mx-auto mt-14 max-w-2xl rounded-3xl border border-forest/15 bg-white/50 p-6 md:p-10"
                >
                  <p className="font-display text-2xl text-forest">Your order</p>
                  <p className="mt-2 text-sm text-ink/70" data-testid="mealprep-order-summary">
                    {plan
                      ? `${plan.name} — ${money(plan.price_cents)} · delivery included${mealCount ? ` · ${filled}/${mealCount} meals chosen` : ""}`
                      : "Pick a plan above to begin."}
                  </p>
                  {mealCount && (
                    <div className="mt-6 rounded-2xl border border-gold/40 bg-white/70 p-5" data-testid="mealprep-mix-picker">
                      <p className="text-xs font-medium uppercase tracking-[0.18em] text-terracotta">
                        Choose your {mealCount} meals — split them across the dishes however you like
                      </p>
                      <div className="mt-4 space-y-4">
                        {(menu.meals || []).map((m) => (
                          <div key={m.id || m.name} className="flex items-center justify-between gap-4">
                            <div className="min-w-0">
                              <p className="truncate text-sm font-medium text-forest">{m.name}</p>
                              {m.description && <p className="mt-0.5 text-xs leading-relaxed text-ink/60">{m.description}</p>}
                            </div>
                            <div className="flex shrink-0 items-center gap-3">
                              <button
                                type="button"
                                aria-label={`Fewer of ${m.name}`}
                                onClick={() => setQty(m.id, (alloc[m.id] || 0) - 1)}
                                data-testid={`mealprep-qty-minus-${m.id}`}
                                className="h-9 w-9 cursor-pointer rounded-full border border-forest/20 text-forest transition-colors hover:bg-forest hover:text-cream"
                              >
                                −
                              </button>
                              <span data-testid={`mealprep-qty-${m.id}`} className="w-6 text-center text-sm font-medium text-forest">
                                {alloc[m.id] || 0}
                              </span>
                              <button
                                type="button"
                                aria-label={`More of ${m.name}`}
                                onClick={() => setQty(m.id, (alloc[m.id] || 0) + 1)}
                                data-testid={`mealprep-qty-plus-${m.id}`}
                                className="h-9 w-9 cursor-pointer rounded-full border border-forest/20 text-forest transition-colors hover:bg-forest hover:text-cream"
                              >
                                +
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="mt-4 text-sm text-ink/70" data-testid="mealprep-mix-status">
                        {filled === mealCount
                          ? "All set — your plan is fully chosen."
                          : `${filled} of ${mealCount} chosen — ${mealCount - filled} to go.`}
                      </p>
                    </div>
                  )}
                  <div className="mt-6 grid gap-4 md:grid-cols-2">
                    <input required placeholder="Your name" value={form.name} onChange={set("name")} data-testid="mealprep-name-input" className="field" />
                    <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} data-testid="mealprep-email-input" className="field" />
                    <input type="tel" placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} data-testid="mealprep-phone-input" className="field" />
                    <div>
                      <label className="field-label" htmlFor="mp-date">Delivery date (Mondays only)</label>
                      <input id="mp-date" type="date" min={today} value={form.delivery_date} onChange={onDate} data-testid="mealprep-date-input" className="field" />
                    </div>
                    <textarea rows="3" placeholder="Allergies, restrictions, or notes…" value={form.delivery_notes} onChange={set("delivery_notes")} data-testid="mealprep-notes-textarea" className="field resize-y md:col-span-2" />
                  </div>
                  {error && (
                    <p role="alert" data-testid="mealprep-error-alert" className="mt-4 rounded-lg bg-terracotta/10 px-4 py-3 text-sm text-terracotta">
                      {error}
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={busy || !planId || !mixReady}
                    data-testid="mealprep-order-submit"
                    className="mt-6 w-full cursor-pointer rounded-full bg-terracotta px-7 py-3.5 text-sm font-medium tracking-wide text-cream transition-[background-color,color,transform] duration-300 hover:-translate-y-0.5 hover:bg-gold hover:text-deepforest disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {busy ? "Taking your order…" : plan ? `Order the ${plan.name} · ${money(total)}` : "Select a plan to order"}
                  </button>
                  <p className="mt-4 text-center text-sm text-ink/60">
                    Questions? DM{" "}
                    <a href={SITE.instagramUrl} target="_blank" rel="noopener noreferrer" className="underline decoration-gold underline-offset-4" data-testid="mealprep-instagram-link">
                      @Sita_Earthfound
                    </a>
                  </p>
                </form>
              </FadeUp>
            </>
          )}
        </div>
      </section>

      {/* ---------- Past menus ---------- */}
      {archive.length > 0 && (
        <section data-testid="mealprep-archive" className="bg-deepforest text-cream">
          <div className="mx-auto max-w-7xl px-6 py-16 md:px-10 md:py-24">
            <FadeUp>
              <Overline light>Archive</Overline>
              <h2 className="mt-4 font-display text-4xl font-light md:text-5xl">Past menus</h2>
            </FadeUp>
            <div className="mt-10 grid gap-6 md:grid-cols-2">
              {archive.slice(0, 6).map((m, i) => (
                <FadeUp key={m.id} delay={i * 0.08}>
                  <div className="h-full rounded-3xl border border-cream/15 p-7" data-testid={`mealprep-archive-${i + 1}`}>
                    <p className="font-display text-xl italic text-gold">{m.week_label}</p>
                    <ul className="mt-4 space-y-1.5 text-sm text-cream/80">
                      {(m.meals || m.items || []).map((it, j) => (
                        <li key={j}>{"{"} {it.name} {"}"}{it.description ? ` — ${it.description}` : ""}</li>
                      ))}
                    </ul>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>
      )}

      <ReviewStrip />
    </div>
  );
}
